import { useCallback, useEffect, useRef } from 'react'
import { WithBodyMessage } from '@weblite/ts-action/message'
import {
  cancelSetDraft,
  deleteDraft,
  getDraft,
  setDraft,
  setImmediateDraft,
} from '$logic/draft'
import { getMessageByIid } from '$logic/message'
import { SendFieldAction } from './send-field.type'
import { editorAPI } from './components/text-editor'
import { getOpenedRoomId } from '$logic/opened-room'
import { isPhoneOrTablet } from '$func/environmentInfo'

interface Props {
  roomId: string
  action: SendFieldAction
  setAction: (action: SendFieldAction) => unknown
  onTypingEvent: () => unknown
}

export function useDraft({ roomId, setAction, action, onTypingEvent }: Props) {
  const prevTextRef = useRef<string>('')

  useEffect(() => {
    if (!roomId) return

    const draft = getDraft(roomId)
    const { body, type, iid, forward } = draft?.value || {}

    if (body) editorAPI.setText(body)
    else editorAPI.setText('')

    if (isPhoneOrTablet) editorAPI.blur()

    if (type === 'EDIT') {
      if (!iid) return
      getMessageByIid(roomId, iid).then(message => {
        if (message && !message.patches?.deleted) {
          setAction({ type, message: message as WithBodyMessage })
        }
      })
    } else if (type === 'FORWARD') {
      if (forward) setAction({ type, forward })
    } else if (type === 'REPLY') {
      if (iid) setAction({ type, iid })
    } else setAction({ type })

    // eslint-disable-next-line consistent-return
    return () => {
      cancelSetDraft(roomId)

      const text = prevTextRef.current
      const roomDraft = getDraft(roomId)?.value.body

      if (text === roomDraft) return
      const draftMeta =
        action.type === 'EDIT'
          ? { type: action.type, iid: action.message.iid }
          : action

      if (text.trim()) setImmediateDraft(roomId, { body: text, ...draftMeta })
    }
  }, [roomId])

  const onDraftChange = useCallback(
    (text: string) => {
      prevTextRef.current = text

      if (getDraft(roomId)?.value.body !== text) {
        const draftMeta =
          action.type === 'EDIT'
            ? { type: action.type, iid: action.message.iid }
            : action
        if (text.trim()) {
          setDraft(roomId, { body: text, ...draftMeta })

          onTypingEvent()
        } else deleteDraft(getOpenedRoomId())
      }
    },
    [action, roomId, onTypingEvent],
  )

  return { onDraftChange }
}
